import get from 'lodash/get.js';

import BaseInterceptor from '@/router/interceptors/BaseInterceptor.js';
import ImpersonationExpiryMonitor from '@/services/ImpersonationExpiryMonitor.js';
import loadingService from '@/services/LoadingService.js';
import { buildAccessDeniedLocation, resolveIntendedRouteForAccessDenied } from '@/utilities/accessDeniedLocation.js';
import { DUE_LOCKED_SURVEY_CODE, noteServerReportedDueLock } from '@/utilities/automatedSurveyLockSchedule.js';

class AccessDeniedInterceptor extends BaseInterceptor {
    handle(status, error) {
        // Concurrent requests can 403 after impersonation is force-stopped
        // server-side; don't flash the 403 page while the expiry redirect runs.
        if (ImpersonationExpiryMonitor.isRedirectInProgress()) {
            return;
        }

        // The server gates progression while a locked survey is due but unstarted.
        // Don't interrupt the current page or show the restricted page: explain the
        // refusal, and let the learner's next navigation move them into the survey.
        if (status === 403 && get(error, 'response.data.code') === DUE_LOCKED_SURVEY_CODE) {
            noteServerReportedDueLock();
            this.notifyBlockedByDueLock(get(error, 'response.data.message'));

            return;
        }

        const routeMatch = ['/login'].includes(this.$router.currentRoute.value.path);

        if ((status === 403 || status === 412) && !routeMatch) {
            if (this.$router.currentRoute.value.name !== '403' && get(error.config, 'errorHandle') !== false) {
                loadingService.closeAll();

                const intended = resolveIntendedRouteForAccessDenied(this.$router);

                this.$router.push(buildAccessDeniedLocation(intended));
            }
        }
    }

    /**
     * @param {string} [message] Server message from the `automated_survey_locked` response.
     */
    notifyBlockedByDueLock(message) {
        // Loaded lazily: the start path pulls in the store and ORM models.
        return import('@/utilities/automatedSurveyStart.js')
            .then(({ notifyBlockedByDueLock }) => notifyBlockedByDueLock(message))
            .catch(importError => {
                console.error('[AccessDeniedInterceptor] Unable to show the due locked survey notice:', importError);
            });
    }
}

export default AccessDeniedInterceptor;
